/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
} from "react";

type Validator = {
  validate: () => boolean;
  onInvalid: () => void;
};

const FormContext = createContext<
  MutableRefObject<Map<unknown, Validator>> | undefined
>(undefined);

/**
 * Form component that prevents submission until every field is valid.
 *
 * If multiple fields share the same state object,
 * consumers must use functional state updates to avoid race conditions.
 */
export const ExperimentalForm = ({
  onSubmit,
  children,
}: {
  /**
   * A callback function that is invoked when the form is submitted and all form fields are valid.
   */
  onSubmit: () => void;
  /**
   * Content of form
   */
  children: ReactNode;
}) => {
  const validators = useRef(new Map<unknown, Validator>());

  return (
    <form
      css={{ display: "contents" }}
      onSubmit={(e) => {
        e.preventDefault();

        let isValid = true;

        for (const { validate, onInvalid } of validators.current.values()) {
          if (!validate()) {
            isValid = false;
            onInvalid();
          }
        }

        if (isValid) {
          onSubmit();
        }
      }}
    >
      <FormContext.Provider value={validators}>{children}</FormContext.Provider>
    </form>
  );
};

/**
 * Registers a field in `ExperimentalForm` and shows error before submit.
 */
export const useSubmitGuard = ({
  validate,
  onInvalid,
}: {
  /**
   * A function that returns `true` if the field is valid.
   */
  validate: () => boolean;
  /**
   * Callback the form calls when the field is invalid.
   */
  onInvalid: () => void;
}) => {
  const [id] = useState({});
  const validatorsRef = useContext(FormContext);

  useEffect(() => {
    if (!validatorsRef) {
      return;
    }

    const validators = validatorsRef.current;

    validators.set(id, { validate, onInvalid });

    return () => {
      validators.delete(id);
    };
  }, [validate, validatorsRef, onInvalid, id]);
};
